import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Pencil, Archive, RotateCcw, KeyRound, X, Building2, CreditCard, Loader2, Copy, ExternalLink, RefreshCw, Ban, Mail, Rocket } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge, PaymentBadge, TrialBadge, Spinner } from "@/components/dashboard/DashBits";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const empty = {
  name: "", plan: "starter", profiles_purchased: 0,
  subscription_status: "active", subscription_end: "", billing_cycle: "monthly",
  payment_status: "manual", premium_access: false,
  user_email: "", user_password: "",
};

function StripePanel({ companyId, companyName, planKey, planLabel, cycle, isFree }) {
  const { t } = useI18n();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = (refresh = false) =>
    api.get(`/admin/companies/${companyId}/stripe${refresh ? "?refresh=true" : ""}`).then(({ data }) => setSt(data));
  useEffect(() => { load(); }, [companyId]);

  const subscribe = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/companies/${companyId}/stripe/subscribe`, { plan_key: planKey, billing_cycle: cycle });
      toast.success(t("dash.stripeSubscribed"));
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm(t("dash.stripeCancelConfirm"))) return;
    setBusy(true);
    try { await api.post(`/admin/companies/${companyId}/stripe/cancel`); toast.success(t("dash.stripeCanceled")); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const copyLink = () => { navigator.clipboard.writeText(st.stripe_checkout_url); toast.success(t("dash.stripeCopied")); };
  const doSend = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/companies/${companyId}/stripe/send-payment-link`, { plan_key: planKey, billing_cycle: cycle });
      toast.success(t("dash.stripeEmailSent"));
      setConfirmOpen(false);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (isFree) return (
    <div className="pt-3 border-t border-white/10">
      <div className="rounded-xl bg-cyan-500/10 border border-cyan-500/20 px-3.5 py-2.5 text-sm text-cyan-300" data-testid="stripe-free-note">{t("dash.stripeFreeNote")}</div>
    </div>
  );
  if (!st) return null;
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
  const connected = !!st.stripe_customer_id;

  return (
    <div className="pt-3 border-t border-white/10 space-y-3" data-testid="stripe-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs font-mono uppercase tracking-wider text-cyan-400 flex items-center gap-2"><CreditCard className="w-3.5 h-3.5" /> {t("dash.stripeSection")}</p>
        <span className={`text-xs ${connected ? "text-emerald-400" : "text-zinc-500"}`} data-testid="stripe-connection">{connected ? t("dash.stripeConnected") : t("dash.stripeNotConnected")}</span>
      </div>
      {connected && (
        <div className="grid grid-cols-2 gap-2 text-xs text-zinc-400">
          <div>{t("dash.stripeSub")} : <span className="text-white">{st.stripe_subscription_id ? "✓" : "—"}</span></div>
          <div>{t("dash.paymentStatus")} : <span className="text-white" data-testid="stripe-pay-status">{t(`dash.pm_${st.payment_status}`)}</span></div>
          <div>{t("dash.stripeNextRenewal")} : <span className="text-white">{fmtDate(st.subscription_current_period_end)}</span></div>
          {st.subscription_cancel_scheduled && <div className="text-amber-400" data-testid="stripe-cancel-scheduled">{t("dash.stripeCancelScheduled")}</div>}
        </div>
      )}
      {st.stripe_checkout_url && (
        <div className="rounded-xl bg-white/5 border border-white/10 p-3 space-y-2" data-testid="stripe-checkout-link">
          <p className="text-xs text-zinc-400">{t("dash.stripePaymentLink")}</p>
          <div className="flex gap-2">
            <input readOnly value={st.stripe_checkout_url} className="flex-1 min-w-0 bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-cyan-300 truncate" data-testid="stripe-checkout-url" />
            <button type="button" onClick={copyLink} data-testid="stripe-copy-link" className="p-2 rounded-lg glass text-zinc-300 hover:text-white shrink-0"><Copy className="w-4 h-4" /></button>
            <a href={st.stripe_checkout_url} target="_blank" rel="noreferrer" className="p-2 rounded-lg glass text-zinc-300 hover:text-white shrink-0"><ExternalLink className="w-4 h-4" /></a>
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={subscribe} disabled={busy} data-testid="stripe-subscribe"
          className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-gradient text-white text-sm font-semibold py-2.5 rounded-xl disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} {t("dash.stripeLaunch")}
        </button>
        <button type="button" onClick={() => load(true)} data-testid="stripe-refresh" className="p-2.5 rounded-xl glass text-zinc-300 hover:text-white"><RefreshCw className="w-4 h-4" /></button>
        {st.stripe_subscription_id && !st.subscription_cancel_scheduled && st.subscription_status !== "expired" && (
          <button type="button" onClick={cancel} data-testid="stripe-cancel" className="p-2.5 rounded-xl glass text-amber-400 hover:bg-amber-500/10"><Ban className="w-4 h-4" /></button>
        )}
      </div>
      {(st.payment_status || "manual") !== "manual" && (
        <button type="button" onClick={() => setConfirmOpen(true)} data-testid="stripe-send-email"
          className="w-full inline-flex items-center justify-center gap-2 glass text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-white/10 disabled:opacity-60 transition-colors duration-300">
          <Mail className="w-4 h-4" /> {t("dash.stripeSendEmail")}
        </button>
      )}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="glass-strong border border-white/10 text-white sm:rounded-2xl" data-testid="stripe-send-dialog">
          <DialogHeader>
            <DialogTitle className="text-white font-display">{t("dash.stripeDlgTitle")}</DialogTitle>
            <DialogDescription className="text-zinc-400">{t("dash.stripeDlgDesc")}</DialogDescription>
          </DialogHeader>
          <div className="rounded-xl bg-white/5 border border-white/10 p-3.5 space-y-2 text-sm">
            <div className="flex items-center justify-between gap-3"><span className="text-zinc-400">{t("dash.stripeDlgCompany")}</span><span className="text-white font-medium truncate" data-testid="dialog-company">{companyName}</span></div>
            <div className="flex items-center justify-between gap-3"><span className="text-zinc-400">{t("dash.stripeDlgRecipient")}</span><span className="text-cyan-300 truncate" data-testid="dialog-email">{st.client_email || "—"}</span></div>
            <div className="flex items-center justify-between gap-3"><span className="text-zinc-400">{t("dash.stripeDlgPlan")}</span><span className="text-white" data-testid="dialog-plan">{planLabel}</span></div>
            <div className="flex items-center justify-between gap-3"><span className="text-zinc-400">{t("dash.stripeDlgCycle")}</span><span className="text-white" data-testid="dialog-cycle">{cycle === "yearly" ? t("dash.billYearly") : t("dash.billMonthly")}</span></div>
          </div>
          <DialogFooter className="gap-2">
            <button type="button" onClick={() => setConfirmOpen(false)} disabled={busy} data-testid="dialog-cancel"
              className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl glass text-zinc-300 hover:text-white text-sm font-medium disabled:opacity-60">
              {t("dash.stripeDlgCancel")}
            </button>
            <button type="button" onClick={doSend} disabled={busy} data-testid="dialog-confirm-send"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-brand-gradient text-white text-sm font-semibold disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} {t("dash.stripeSendEmail")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CompanyModal({ initial, plans, onClose, onSaved }) {
  const { t } = useI18n();
  const editing = !!initial?.id;
  const [form, setForm] = useState(initial ? { ...empty, ...initial } : empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value }));
  const inputCls = "w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm placeholder:text-zinc-600 focus-ring";

  const resetPw = async () => {
    const np = window.prompt(t("dash.newPassword"));
    if (!np) return;
    try {
      await api.post(`/admin/companies/${initial.id}/reset-password`, { new_password: np });
      toast.success(t("dash.passwordReset"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const startTrial = async () => {
    if (!window.confirm(t("dash.trialConfirm"))) return;
    try {
      await api.post(`/admin/companies/${initial.id}/start-trial`);
      toast.success(t("dash.trialStarted"));
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const changeEmail = async () => {
    const ne = window.prompt(t("dash.newEmailPrompt"), "");
    if (!ne) return;
    try {
      await api.put(`/admin/companies/${initial.id}/email`, { new_email: ne });
      toast.success(t("dash.emailChanged"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        await api.put(`/admin/companies/${initial.id}`, {
          name: form.name, plan: form.plan, profiles_purchased: form.profiles_purchased,
          subscription_status: form.subscription_status,
          subscription_end: form.subscription_status === "free" ? null : (form.subscription_end || null),
          billing_cycle: form.billing_cycle, payment_status: form.payment_status,
          premium_access: !!form.premium_access,
        });
      } else {
        await api.post("/admin/companies", form);
      }
      toast.success(editing ? t("dash.companyUpdated") : t("dash.companyCreated"));
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="company-modal">
      <div className="glass-strong rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 glass-strong">
          <h3 className="font-display font-bold text-white">{editing ? t("dash.editCompany") : t("dash.newCompany")}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-3.5">
          <input required placeholder={t("dash.companyName")} value={form.name} onChange={set("name")} className={inputCls} data-testid="company-name" />
          <div className="grid grid-cols-2 gap-3">
            <select value={form.plan} onChange={set("plan")} className={inputCls} data-testid="company-plan">
              {plans.map((p) => <option key={p.key} value={p.key} className="bg-[#14141f]">{p.name} ({p.max_profiles})</option>)}
            </select>
            <input type="number" min={0} placeholder={t("dash.purchased")} value={form.profiles_purchased} onChange={set("profiles_purchased")} className={inputCls} data-testid="company-purchased" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select value={form.subscription_status} onChange={set("subscription_status")} className={inputCls} data-testid="company-status">
              <option value="active" className="bg-[#14141f]">{t("dash.active")}</option>
              <option value="expiring" className="bg-[#14141f]">{t("dash.expiring")}</option>
              <option value="expired" className="bg-[#14141f]">{t("dash.expired")}</option>
              <option value="suspended" className="bg-[#14141f]">{t("dash.suspended")}</option>
              <option value="free" className="bg-[#14141f]">{t("dash.free")}</option>
            </select>
            <select value={form.billing_cycle} onChange={set("billing_cycle")} className={inputCls}>
              <option value="monthly" className="bg-[#14141f]">{t("dash.billMonthly")}</option>
              <option value="yearly" className="bg-[#14141f]">{t("dash.billYearly")}</option>
            </select>
          </div>
          {form.subscription_status === "free" ? (
            <div className="rounded-xl bg-cyan-500/10 border border-cyan-500/20 px-3.5 py-2.5 text-sm text-cyan-300" data-testid="company-no-expiration">
              {t("dash.noExpiration")}
            </div>
          ) : (
            <div>
              <label className="text-xs text-zinc-500">{t("dash.subEnd")}</label>
              <input type="date" value={form.subscription_end ? form.subscription_end.slice(0, 10) : ""} onChange={set("subscription_end")} className={inputCls} />
            </div>
          )}
          <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 px-3.5 py-3 cursor-pointer" data-testid="company-premium-label">
            <input type="checkbox" checked={!!form.premium_access} onChange={(e) => setForm((f) => ({ ...f, premium_access: e.target.checked }))} data-testid="company-premium-access" className="mt-0.5 w-4 h-4 accent-cyan-500" />
            <span className="text-sm">
              <span className="text-white font-medium">{t("dash.premiumAccess")}</span>
              <span className="block text-xs text-zinc-500 mt-0.5">{t("dash.premiumAccessHint")}</span>
            </span>
          </label>
          {editing && (
            <div>
              <label className="text-xs text-zinc-500">{t("dash.paymentStatus")}</label>
              <select value={form.payment_status || "manual"} onChange={set("payment_status")} className={inputCls} data-testid="company-payment-status">
                <option value="manual" className="bg-[#14141f]">{t("dash.pm_manual")}</option>
                <option value="active" className="bg-[#14141f]">{t("dash.pm_active")}</option>
                <option value="past_due" className="bg-[#14141f]">{t("dash.pm_past_due")}</option>
                <option value="failed" className="bg-[#14141f]">{t("dash.pm_failed")}</option>
                <option value="canceled" className="bg-[#14141f]">{t("dash.pm_canceled")}</option>
              </select>
              {!initial.stripe_customer_id && <p className="mt-1 text-xs text-zinc-500">{t("dash.stripeNotLinked")}</p>}
            </div>
          )}
          {editing && (
            <div className="pt-3 border-t border-white/10 space-y-2.5" data-testid="trial-panel">
              <p className="text-xs font-mono uppercase tracking-wider text-cyan-400">{t("dash.trialTitle")}</p>
              <div className="flex items-center justify-between text-sm">
                <span className="text-zinc-400">{t("dash.trialStatus")}</span>
                <TrialBadge state={initial.trial_state} days={initial.trial_days_remaining} />
              </div>
              {initial.trial_started_at && (
                <div className="text-xs text-zinc-500 space-y-1">
                  <div>{t("dash.trialStart")}: {new Date(initial.trial_started_at).toLocaleDateString("fr-FR")}</div>
                  <div>{t("dash.trialEnd")}: {new Date(initial.trial_ends_at).toLocaleDateString("fr-FR")}</div>
                  {initial.trial_state === "trial_active" && <div className="text-cyan-300">{initial.trial_days_remaining} {t("dash.trialDaysLeft")}</div>}
                </div>
              )}
              {initial.trial_state === "preparation" && (
                <button type="button" onClick={startTrial} data-testid="start-trial-btn"
                  className="w-full inline-flex items-center justify-center gap-2 bg-brand-gradient text-white text-sm font-semibold py-2.5 rounded-xl">
                  <Rocket className="w-4 h-4" /> {t("dash.startTrial")}
                </button>
              )}
              <button type="button" onClick={changeEmail} data-testid="change-email-btn"
                className="w-full inline-flex items-center justify-center gap-2 glass text-white text-sm font-medium py-2.5 rounded-xl hover:bg-white/10 transition-colors duration-300">
                <Mail className="w-4 h-4" /> {t("dash.changeEmail")}
              </button>
            </div>
          )}
          {editing && (
            <StripePanel companyId={initial.id} companyName={initial.name} planKey={form.plan} planLabel={(plans.find((p) => p.key === form.plan) || {}).name || form.plan} cycle={form.billing_cycle} isFree={form.subscription_status === "free"} />
          )}
          {!editing && (
            <div className="pt-3 border-t border-white/10 space-y-3.5">
              <p className="text-xs font-mono uppercase tracking-wider text-cyan-400">{t("dash.clientAccount")}</p>
              <input required type="email" placeholder={t("dash.loginEmail")} value={form.user_email} onChange={set("user_email")} className={inputCls} data-testid="company-user-email" />
              <input required type="text" placeholder={t("dash.passwordField")} value={form.user_password} onChange={set("user_password")} className={inputCls} data-testid="company-user-password" />
            </div>
          )}
          {editing && (
            <button type="button" onClick={resetPw} data-testid="company-reset-password"
              className="w-full inline-flex items-center justify-center gap-2 glass text-white font-medium py-2.5 rounded-xl hover:bg-white/10 transition-colors duration-300">
              <KeyRound className="w-4 h-4" /> {t("dash.resetPassword")}
            </button>
          )}
          <button type="submit" disabled={loading} data-testid="company-save"
            className="w-full bg-brand-gradient text-white font-semibold py-3 rounded-xl disabled:opacity-60">
            {loading ? t("dash.saving") : editing ? t("dash.save") : t("dash.createCompany")}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Companies() {
  const { t } = useI18n();
  const [companies, setCompanies] = useState(null);
  const [plans, setPlans] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [modal, setModal] = useState(null);

  const load = () => {
    api.get(`/admin/companies?search=${encodeURIComponent(search)}&status=${status}`).then(({ data }) => setCompanies(data));
  };
  useEffect(() => { api.get("/admin/plans").then(({ data }) => setPlans(data)); }, []);
  useEffect(() => { const tm = setTimeout(load, 250); return () => clearTimeout(tm); }, [search, status]);

  const archive = async (c) => {
    if (!window.confirm(t("dash.confirmArchiveCompany", { name: c.name }))) return;
    await api.delete(`/admin/companies/${c.id}`);
    toast.success(t("dash.companyArchived"));
    load();
  };

  const restore = async (c) => {
    await api.post(`/admin/companies/${c.id}/restore`);
    toast.success(t("dash.companyRestored"));
    load();
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl text-white">{t("dash.companiesTitle")}</h1>
          <p className="mt-1 text-zinc-400">{t("dash.companiesSub")}</p>
        </div>
        <button onClick={() => setModal(empty)} data-testid="company-new"
          className="inline-flex items-center gap-2 bg-brand-gradient text-white font-semibold px-4 py-2.5 rounded-full">
          <Plus className="w-4 h-4" /> {t("dash.newCompany")}
        </button>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input placeholder={t("dash.searchCompany")} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="company-search"
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-white text-sm focus-ring" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="company-filter"
          className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus-ring">
          <option value="" className="bg-[#14141f]">{t("dash.allStatus")}</option>
          <option value="active" className="bg-[#14141f]">{t("dash.active")}</option>
          <option value="expiring" className="bg-[#14141f]">{t("dash.expiring")}</option>
          <option value="expired" className="bg-[#14141f]">{t("dash.expired")}</option>
          <option value="suspended" className="bg-[#14141f]">{t("dash.suspended")}</option>
          <option value="free" className="bg-[#14141f]">{t("dash.free")}</option>
          <option value="archived" className="bg-[#14141f]">{t("dash.archivedFilter")}</option>
        </select>
      </div>

      {companies === null ? <Spinner /> : (
        <div className="mt-6 space-y-3" data-testid="company-list">
          {companies.length === 0 && <p className="text-zinc-500 py-10 text-center">{t("dash.noCompanies")}</p>}
          {companies.map((c) => (
            <div key={c.id} className="rounded-2xl border border-white/10 bg-[#0d0d14] p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`company-row-${c.slug}`}>
              <span className="w-11 h-11 rounded-xl bg-brand-gradient flex items-center justify-center shrink-0"><Building2 className="w-5 h-5 text-white" /></span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="font-display font-semibold text-white">{c.name}</p>
                  <StatusBadge status={c.archived ? "archived" : c.subscription_status} />
                  {!c.archived && c.subscription_status !== "free" && <PaymentBadge status={c.payment_status || "manual"} testid={`company-payment-${c.slug}`} />}
                  {!c.archived && <TrialBadge state={c.trial_state} days={c.trial_days_remaining} />}
                  <span className="text-xs font-mono text-zinc-500 uppercase">{c.plan}</span>
                </div>
                <p className="text-sm text-zinc-400 mt-1">
                  {c.usage.active} {t("dash.actives")} / {c.usage.purchased} {t("dash.boughtShort")} · {c.usage.available} {t("dash.availableShort")} · {t("dash.maxShort")} {c.usage.max_profiles}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setModal(c)} data-testid={`company-edit-${c.slug}`} className="p-2 rounded-lg glass text-zinc-300 hover:text-white"><Pencil className="w-4 h-4" /></button>
                {c.archived ? (
                  <button onClick={() => restore(c)} data-testid={`company-restore-${c.slug}`} className="p-2 rounded-lg glass text-emerald-400 hover:bg-emerald-500/10"><RotateCcw className="w-4 h-4" /></button>
                ) : (
                  <button onClick={() => archive(c)} data-testid={`company-archive-${c.slug}`} className="p-2 rounded-lg glass text-amber-400 hover:bg-amber-500/10"><Archive className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && <CompanyModal initial={modal} plans={plans} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}
